/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { COGNITO_GROUPS } from 'utils/const';

// ------------------------------------
// State
// ------------------------------------

const initialState = {
  // source documents
  imd: { view: false, upload: false, download: false, delete: false },
  vin: { view: false, upload: false, download: false, delete: false },
  ppl: { view: false, upload: false, download: false, delete: false },
  priceMatrix: { view: false, upload: false, download: false, delete: false },
  nomenclatureGuide: {
    view: false,
    upload: false,
    download: false,
    delete: false,
  },
  techSpecs: { view: false, upload: false, download: false, delete: false },
  // generated documents
  taxImport: { view: false, update: false, download: false, delete: false },
  pcdf: { view: false, update: false, download: false, delete: false },
  toolbox: { view: false, update: false, download: false, delete: false },
  pfa: { view: false, update: false, download: false, delete: false }, // price calculator preview
  mvdf: { view: false, update: false, download: false, delete: false },
  priceGuide: {
    view: false,
    update: false,
    download: false,
    delete: false,
    publish: false,
  },
  vehicleTechSpecs: {
    view: false,
    update: false,
    download: false,
    delete: false,
    publish: false,
  },
  topFeatures: { view: false, update: false, download: false, delete: false },
  featuresGuide: { view: false, update: false, download: false, delete: false },
  // common actions
  download: false,
  generate: false,
  publish: false,
  publishToUAT: false,
};

const permissions = {
  admin: {
    imd: { view: true, upload: true, download: true },
    vin: { view: true, upload: true, download: true },
    ppl: { view: true, upload: true, download: true, delete: true },
    priceMatrix: { view: true, upload: true, download: true },
    nomenclatureGuide: { view: true, upload: true, download: true },
    techSpecs: { view: true, upload: true, download: true },
    pcdf: { view: true, update: true, download: true },
    taxImport: { view: true, update: true, download: true },
    toolbox: { view: true, update: true, download: true },
    pfa: { view: true, update: true, download: true },
    mvdf: { view: true, update: true, download: true },
    vehicleTechSpecs: { view: true, update: true, download: true },
    priceGuide: { view: true, update: true, download: true },
    topFeatures: { view: true, update: true, download: true },
    featuresGuide: { view: true, update: true, download: true },
    download: true,
    generate: true,
    publish: true,
    publishToUAT: true,
  },
  incentives: {
    viewer: {
      imd: { view: true, upload: false, download: true, delete: false },
      ppl: { view: true, upload: false, download: true, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      pcdf: { view: true, update: false, download: true, delete: false },
      taxImport: { view: true, update: false, download: true, delete: false },
      toolbox: { view: true, update: false, download: true, delete: false },
      pfa: { view: true, update: false, download: true, delete: false },
      download: true,
      generate: false,
      publish: false,
      publishToUAT: false,
    },
    editor: {
      imd: { view: true, upload: true, download: true, delete: false },
      ppl: { view: true, upload: false, download: true, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      pcdf: { view: true, update: true, download: true, delete: false },
      taxImport: { view: true, update: true, download: true, delete: false },
      toolbox: { view: true, update: true, download: true, delete: false },
      pfa: { view: true, update: true, download: true, delete: false },
      download: true,
      generate: true,
      publish: false,
      publishToUAT: false,
    },
    uatPublisher: {
      imd: { view: true, upload: false, download: false, delete: false },
      ppl: { view: true, upload: false, download: false, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      pcdf: { view: true, update: false, download: false, delete: false },
      taxImport: {
        view: true,
        update: false,
        download: false,
        delete: false,
      },
      toolbox: { view: true, update: false, download: false, delete: false },
      pfa: { view: true, update: false, download: false, delete: false },
      download: false,
      generate: false,
      publish: false,
      publishToUAT: true,
    },
    publisher: {
      imd: { view: true, upload: false, download: false, delete: false },
      ppl: { view: true, upload: false, download: false, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      pcdf: { view: true, update: false, download: false, delete: false },
      taxImport: {
        view: true,
        update: false,
        download: false,
        delete: false,
      },
      toolbox: { view: true, update: false, download: false, delete: false },
      pfa: { view: true, update: false, download: false, delete: false },
      download: false,
      generate: false,
      publish: true,
      publishToUAT: false,
    },
    superPublisher: {
      imd: { view: true, upload: false, download: false, delete: false },
      ppl: { view: true, upload: false, download: false, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      pcdf: { view: true, update: false, download: false, delete: false },
      taxImport: {
        view: true,
        update: false,
        download: false,
        delete: false,
      },
      toolbox: { view: true, update: false, download: false, delete: false },
      pfa: { view: true, update: false, download: false, delete: false },
      download: false,
      generate: false,
      publish: true,
      publishToUAT: true,
    },
    publisherAndEditor: {
      imd: { view: true, upload: true, download: true, delete: false },
      ppl: { view: true, upload: false, download: true, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      pcdf: { view: true, update: true, download: true, delete: false },
      taxImport: { view: true, update: true, download: true, delete: false },
      toolbox: { view: true, update: true, download: true, delete: false },
      pfa: { view: true, update: true, download: true, delete: false },
      download: true,
      generate: true,
      publish: true,
      publishToUAT: false,
    },
    uatPublisherAndEditor: {
      imd: { view: true, upload: true, download: true, delete: false },
      ppl: { view: true, upload: false, download: true, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      pcdf: { view: true, update: true, download: true, delete: false },
      taxImport: { view: true, update: true, download: true, delete: false },
      toolbox: { view: true, update: true, download: true, delete: false },
      pfa: { view: true, update: true, download: true, delete: false },
      download: true,
      generate: true,
      publish: false,
      publishToUAT: true,
    },
    superPublisherAndEditor: {
      imd: { view: true, upload: true, download: true, delete: false },
      ppl: { view: true, upload: false, download: true, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      pcdf: { view: true, update: true, download: true, delete: false },
      taxImport: { view: true, update: true, download: true, delete: false },
      toolbox: { view: true, update: true, download: true, delete: false },
      pfa: { view: true, update: true, download: true, delete: false },
      download: true,
      generate: true,
      publish: true,
      publishToUAT: true,
    },
  },
  productPlanning: {
    viewer: {
      ppl: { view: true, upload: false, download: true, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: true,
        delete: false,
      },
      nomenclatureGuide: {
        view: true,
        upload: false,
        download: true,
        delete: false,
      },
      techSpecs: { view: true, upload: false, download: true, delete: false },
      mvdf: { view: true, update: false, download: true, delete: false },
      vehicleTechSpecs: {
        view: true,
        update: false,
        download: true,
        delete: false,
        publish: false,
      },
      priceGuide: {
        view: true,
        update: false,
        download: true,
        delete: false,
        publish: false,
      },
      topFeatures: {
        view: true,
        update: false,
        download: true,
        delete: false,
      },
      featuresGuide: {
        view: true,
        update: false,
        download: true,
        delete: false,
      },
      download: true,
      generate: false,
      publish: false,
    },
    editor: {
      ppl: { view: true, upload: true, download: true, delete: false },
      priceMatrix: {
        view: true,
        upload: true,
        download: true,
        delete: false,
      },
      nomenclatureGuide: {
        view: true,
        upload: true,
        download: true,
        delete: false,
      },
      techSpecs: { view: true, upload: true, download: true, delete: false },
      mvdf: { view: true, update: true, download: true, delete: false },
      priceGuide: {
        view: true,
        update: true,
        download: true,
        delete: false,
        publish: false,
      },
      topFeatures: {
        view: true,
        update: true,
        download: true,
        delete: false,
      },
      featuresGuide: {
        view: true,
        update: true,
        download: true,
        delete: false,
      },
      vehicleTechSpecs: {
        view: true,
        update: true,
        download: true,
        delete: false,
        publish: false,
      },
      download: true,
      generate: true,
      publish: false,
    },
    publisher: {
      ppl: { view: true, upload: false, download: false, delete: false },
      priceMatrix: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      nomenclatureGuide: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      techSpecs: {
        view: true,
        upload: false,
        download: false,
        delete: false,
      },
      mvdf: { view: true, update: false, download: false, delete: false },
      vehicleTechSpecs: {
        view: true,
        update: false,
        download: false,
        delete: false,
        publish: false,
      },
      priceGuide: {
        view: true,
        update: false,
        download: false,
        delete: false,
        publish: false,
      },
      topFeatures: {
        view: true,
        update: false,
        download: false,
        delete: false,
      },
      featuresGuide: {
        view: true,
        update: false,
        download: false,
        delete: false,
      },
      download: false,
      generate: false,
      publish: true,
    },
    publisherAndEditor: {
      ppl: { view: true, upload: true, download: true, delete: false },
      priceMatrix: {
        view: true,
        upload: true,
        download: true,
        delete: false,
      },
      nomenclatureGuide: {
        view: true,
        upload: true,
        download: true,
        delete: false,
      },
      techSpecs: { view: true, upload: true, download: true, delete: false },
      mvdf: { view: true, update: true, download: true, delete: false },
      priceGuide: {
        view: true,
        update: true,
        download: true,
        delete: false,
        publish: false,
      },
      topFeatures: {
        view: true,
        update: true,
        download: true,
        delete: false,
      },
      vehicleTechSpecs: {
        view: true,
        update: true,
        download: true,
        delete: false,
        publish: false,
      },
      featuresGuide: {
        view: true,
        update: true,
        download: true,
        delete: false,
      },
      download: true,
      generate: true,
      publish: true,
    },
  },
  recallCompliance: {
    superPublisherAndEditor: {
      imd: { view: true, upload: true, download: true, delete: false },
      vin: { view: true, upload: true, download: true, delete: false },
      download: true,
      generate: true,
      publish: true,
      publishToUAT: true,
    },
  },
};

// ------------------------------------
// Slices
// -----------------------------------

const filePermissionsSlice = createSlice({
  name: 'filePermissions',
  initialState,
  reducers: {
    iAmAdmin: () => ({ ...initialState, ...permissions.admin }),
    iAmIncentivesMember: (state, action) => {
      const cognitoGroups = action.payload;
      const assumedRole = cognitoGroups[0];
      let filePermissions = { ...initialState };

      switch (assumedRole) {
        case COGNITO_GROUPS.SUPER_PUBLISHER_AND_EDITOR:
          filePermissions = permissions.incentives.superPublisherAndEditor;
          break;
        case COGNITO_GROUPS.PUBLISHER_AND_EDITOR:
          filePermissions = permissions.incentives.publisherAndEditor;
          break;
        case COGNITO_GROUPS.UAT_PUBLISHER_AND_EDITOR:
          filePermissions = permissions.incentives.uatPublisherAndEditor;
          break;
        case COGNITO_GROUPS.EDITOR:
          filePermissions = permissions.incentives.editor;
          break;
        case COGNITO_GROUPS.VIEWER:
          filePermissions = permissions.incentives.viewer;
          break;
        case COGNITO_GROUPS.SUPER_PUBLISHER:
          filePermissions = permissions.incentives.superPublisher;
          break;
        case COGNITO_GROUPS.UAT_PUBLISHER:
          filePermissions = permissions.incentives.uatPublisher;
          break;
        case COGNITO_GROUPS.PUBLISHER:
          filePermissions = permissions.incentives.publisher;
          break;

        default:
          break;
      }

      return { ...initialState, ...filePermissions };
    },
    iAmProductPlanningMember: (state, action) => {
      const cognitoGroups = action.payload;

      let filePermissions = { ...initialState };
      const assumedRole = cognitoGroups[0];
      switch (assumedRole) {
        case COGNITO_GROUPS.VIEWER:
          filePermissions = permissions.productPlanning.viewer;
          break;
        case COGNITO_GROUPS.EDITOR:
          filePermissions = permissions.productPlanning.editor;
          break;
        case COGNITO_GROUPS.PUBLISHER:
          filePermissions = permissions.productPlanning.publisher;
          break;
        case COGNITO_GROUPS.PUBLISHER_AND_EDITOR:
          filePermissions = permissions.productPlanning.publisherAndEditor;
          break;
        default:
          break;
      }

      return { ...initialState, ...filePermissions };
    },
    iAmRecallComplianceMember: (state, action) => {
      const cognitoGroups = action.payload;

      let filePermissions = { ...initialState };
      const assumedRole = cognitoGroups[0];
      switch (assumedRole) {
        case COGNITO_GROUPS.SUPER_PUBLISHER:
          filePermissions =
            permissions.recallCompliance.superPublisherAndEditor;
          break;
        default:
          break;
      }

      return { ...initialState, ...filePermissions };
    },
    disableFilePublish: (state) => {
      state.publish = false;
    },
  },
});
// ------------------------------------
// Exports
// ------------------------------------

export default filePermissionsSlice.reducer;
export const {
  iAmAdmin,
  iAmIncentivesMember,
  iAmProductPlanningMember,
  iAmRecallComplianceMember,
  disableFilePublish,
} = filePermissionsSlice.actions;
